window.dataLayer = window.dataLayer || [];
window.QF = window.QF || {};

const PRODUCT_STORE_KEY = "products";
const VARIANT_STORE_KEY = "productVariants";
const TRACKED_CARD_KEY = "trackedProductCardIds";

// Klaviyo signup forms that count as a newsletter opt-in, keyed by Klaviyo form ID.
// Unlisted forms (e.g. the feeding-advice "email my recommendations" box) do not fire sign_up.
const NEWSLETTER_FORM_METHODS = {
  Yfb3My: "newsletter_footer",
  S9QsQ7: "newsletter_page",
  WMT4Jh: "competition",
  XSByEB: "paid_social",
  XmFpc8: "sub_what_to_expect",
};

function pushEcommerce(payload) {
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push(payload);
}

function readJsonStorage(key, fallback = {}) {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch {
    return fallback;
  }
}

function writeJsonStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can fail in private browsing; tracking should continue without it.
  }
}

function clone(value) {
  if (!value || typeof value !== "object") return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return { ...value };
  }
}

function normalizeValue(value) {
  if (value === undefined || value === null) return "";
  return String(value).replace(/[- ]/g, "_").toLowerCase();
}

function camelToUnderscore(value) {
  if (value !== "" && value !== null && value !== undefined) {
    return String(value)
      .trim()
      .replace(/([A-Z])/g, "$1")
      .split(" ")
      .join("_")
      .toLowerCase();
  }
  return "";
}

async function storeProductData() {
  try {
    const response = await fetch("/collections/all?sections=productmap");
    const data = await response.json();
    const rawSection = data.productmap || data["productmap"] || "";
    const jsonChunk = rawSection.split("|||")[1];

    if (!jsonChunk) return;

    const parsed = JSON.parse(jsonChunk.replace(/,\s*$/, "").trim());
    const existingProducts = readJsonStorage(PRODUCT_STORE_KEY);

    Object.entries(parsed.products || {}).forEach(([key, product]) => {
      const existing = existingProducts[key] || {};
      existingProducts[key] = {
        ...product,
        item_list_id: existing.item_list_id,
        item_list_name: existing.item_list_name,
      };
    });

    writeJsonStorage(PRODUCT_STORE_KEY, existingProducts);
    writeJsonStorage(VARIANT_STORE_KEY, parsed.variants || {});
  } catch (error) {
    console.error("Tracking: unable to store product data", error);
  }
}

async function lookupProductData(productId, sellingPlan = null) {
  const productStore = readJsonStorage(PRODUCT_STORE_KEY);
  const variantStore = readJsonStorage(VARIANT_STORE_KEY);
  const resolvedProductId = productStore[productId] ? productId : variantStore[productId];
  const productData = clone(productStore[resolvedProductId]);

  if (!productData) {
    throw new Error(`Product ${productId} data not found in localStorage`);
  }

  const trackedProductCardIds = readJsonStorage(TRACKED_CARD_KEY, []);
  const trackedProduct = trackedProductCardIds.find(
    (product) => String(product.id) === String(resolvedProductId),
  );

  if (trackedProduct && trackedProduct.index !== undefined) {
    productData.index = trackedProduct.index;
  }

  productData.item_category5 =
    sellingPlan !== null && sellingPlan !== undefined ? sellingPlan : "one_time|subscription";

  return productData;
}

function setProductListContext(productCard, index, sectionTitle) {
  const productId = productCard.getAttribute("data-product-id");
  if (!productId) return null;

  const productStore = readJsonStorage(PRODUCT_STORE_KEY);
  const product = productStore[productId];

  if (product) {
    if (sectionTitle) {
      product.item_list_id = sectionTitle;
      product.item_list_name = sectionTitle
        .replace(/_/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
    }
    product.index = index;
    product.quantity = 1;
    productStore[productId] = product;
    writeJsonStorage(PRODUCT_STORE_KEY, productStore);
  }

  return { section: sectionTitle, id: productId, index };
}

function collectProductCards(grid) {
  if (!grid) return [];

  const productCards = Array.from(grid.querySelectorAll(".product-card[data-product-id]"));
  const wrapper = grid.closest("grid-load-more");
  const sectionTitle =
    grid.getAttribute("data-section-title") ||
    wrapper?.getAttribute("data-section") ||
    grid.closest("[data-section-title]")?.getAttribute("data-section-title") ||
    window.QF.page_handle ||
    null;

  const tracked = productCards
    .map((productCard, index) => setProductListContext(productCard, index, sectionTitle))
    .filter(Boolean);

  writeJsonStorage(TRACKED_CARD_KEY, tracked);
  return tracked;
}

async function trackedProductList() {
  const trackedProductCardIds = readJsonStorage(TRACKED_CARD_KEY, []);
  const trackedProducts = await Promise.all(
    trackedProductCardIds.map(async (productCard) => {
      try {
        const productData = await lookupProductData(productCard.id, null);
        productData.index = productCard.index;
        return normalizeProductVariant(productData);
      } catch (error) {
        console.error("Tracking: error looking up product", error);
        return null;
      }
    }),
  );

  const items = trackedProducts.filter(Boolean);
  if (items.length) {
    productList(items);
  }

  try {
    localStorage.removeItem(TRACKED_CARD_KEY);
  } catch {
    // noop
  }
}

function fetchProductCards(root = document) {
  const grids = root.querySelectorAll(
    ".collection-grid, .search-grid, .product-grid, .predictive-search-results__product-grid",
  );

  grids.forEach((grid) => {
    collectProductCards(grid);
    void trackedProductList();
  });
}

function observeSections() {
  if (!("IntersectionObserver" in window)) {
    fetchProductCards();
    return;
  }

  const sections = document.querySelectorAll(
    "[data-section-title]:not(.collection-grid[data-section-title], .search-grid[data-section-title])",
  );

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const sectionTitle = entry.target.getAttribute("data-section-title");
      const productCards = entry.target.querySelectorAll(".product-card[data-product-id]");
      const tracked = Array.from(productCards)
        .map((productCard, index) => setProductListContext(productCard, index, sectionTitle))
        .filter(Boolean);

      writeJsonStorage(TRACKED_CARD_KEY, tracked);
      void trackedProductList();
      obs.unobserve(entry.target);
    });
  });

  sections.forEach((section) => observer.observe(section));
}

function normalizeProductVariant(productData, variantId = null) {
  if (!productData) return null;
  const item = clone(productData);

  if (item.item_variant && typeof item.item_variant === "object") {
    if (variantId && item.item_variant[variantId]) {
      item.item_variant = item.item_variant[variantId].variant_title;
    } else {
      const variants = Object.values(item.item_variant);
      const matchedVariant = variants.find((variant) => variant.sku === item.item_master_sku);
      item.item_variant = matchedVariant ? normalizeValue(matchedVariant.variant_title) : "";
    }
  }

  return item;
}

function trackProductEvent(value, viewType, status, items) {
  pushEcommerce({
    event: "view_item",
    ecommerce: {
      currency: window.QF.currency,
      value,
      view_type: viewType,
      item_status: status,
      items,
    },
  });
}

async function trackProductPageView() {
  if (window.templateName !== "product" || !window.QF.product?.id) return;

  try {
    const productData = await lookupProductData(window.QF.product.id, null);
    const item = normalizeProductVariant(productData, window.QF.variant_id);
    item.item_category5 =
      window.QF.product?.selling_plan_groups?.length > 0 ? "subscription" : "one_time";

    trackProductEvent(item.price, "page", window.QF.product_available, [item]);
  } catch (error) {
    console.error("Tracking: unable to track product view", error);
  }
}

async function trackQuickView(event) {
  const detail = event.detail || {};
  const productId = detail.product_id || detail.item_id;
  const variantId = detail.variant_id;

  try {
    let item;
    if (productId) {
      item = normalizeProductVariant(await lookupProductData(productId, null), variantId);
    } else {
      item = normalizeProductVariant(detail, variantId);
    }

    if (!item) return;

    const itemStatus =
      detail.in_stock === "true" || detail.in_stock === true ? "available" : "unavailable";

    trackProductEvent(detail.price || item.price || "", "quick_view", itemStatus, [item]);
  } catch (error) {
    console.error("Tracking: unable to track quick view", error);
  }
}

function productList(items) {
  const productCards = Array.from(
    document.querySelectorAll(
      ".collection-grid .product-card, .search-grid .product-card, .product-grid .product-card",
    ),
  );

  const itemsWithIndex = items.map((item) => {
    const pageIndex = productCards.findIndex(
      (productCard) => productCard.getAttribute("data-product-id") === String(item.item_id),
    );

    return {
      ...item,
      index: pageIndex !== -1 ? pageIndex : item.index,
    };
  });

  const ecommerceData = {
    item_list_id: window.QF.page_handle || null,
    item_list_name: window.QF.page_title || null,
    item_list_lifestage: window.QF.filter_lifestage || "",
    item_list_product_type: window.QF.filter_product_type || "",
    item_list_range: window.QF.filter_range || "",
    item_list_special_diets: window.QF.filter_special_diets || "",
    item_list_packsize: window.QF.filter_packsize || "",
    item_list_flavour: window.QF.filter_flavour || "",
    item_list_sort: window.QF.sort_by || null,
    item_list_count: window.QF.products_count || null,
    items: itemsWithIndex,
  };

  if (window.template === "page.build-a-box" || window.template === "page.build-a-box-landing") {
    ecommerceData.item_list_build_a_box = true;
    ecommerceData.item_list_bab_answers = window.QF.bab_answers || null;
  }

  pushEcommerce({
    event: "view_list_item",
    ecommerce: ecommerceData,
  });

  if (window.templateName === "search" || window.template === "search") {
    pushEcommerce({
      event: "search",
      ecommerce: {
        search_keywords: window.QF.search_terms || getSearchTerm(),
        search_type: "main-search",
        items: itemsWithIndex,
      },
    });
  }
}

function trackUserState() {
  if (window.QF.logged_in) {
    const userSignUp = localStorage.getItem("userSignUp");
    const userEmail = localStorage.getItem("userEmail");

    if (userSignUp === "true" && userEmail === window.QF.customer?.email) {
      pushEcommerce({
        event: "sign_up",
        ecommerce: {
          method: "create_account",
        },
      });
      localStorage.removeItem("userSignUp");
      localStorage.removeItem("userEmail");
    }

    const userLoggedIn = localStorage.getItem("userLoggedIn");
    if (userLoggedIn === "false" || userLoggedIn === null) {
      localStorage.setItem("userLoggedIn", "true");
      window.dataLayer.push({
        event: "login",
        ecommerce: {
          method: "account_login",
        },
      });
    }

    window.dataLayer.push({
      event: "user",
      user: {
        user_status: "logged-in",
        user_id: window.QF.customer ? window.QF.customer.id || null : null,
        has_subscription: window.QF.has_subscription || null,
        past_purchase: window.QF.past_purchase || null,
      },
    });
    return;
  }

  window.dataLayer.push({
    event: "user",
    user: {
      user_status: "logged-out",
    },
  });

  if (localStorage.getItem("userLoggedIn") === "true") {
    window.dataLayer.push({ event: "logout" });
    localStorage.removeItem("userLoggedIn");
  }
}

function trackFormHash() {
  if (!window.location.hash?.startsWith("#")) return;

  const params = window.location.hash
    .replace("#", "")
    .split("&")
    .reduce((result, item) => {
      const parts = item.split("=");
      result[parts[0]] = parts[1];
      return result;
    }, {});

  if (params.posted_successfully === "user_signup") {
    document.dispatchEvent(new CustomEvent("user_signup", { detail: { method: "user_signup" } }));
  }
}

async function cartDataLayerPush(eventName, cartContents) {
  if (!cartContents?.items) return;

  const itemData = await Promise.all(
    cartContents.items.map((item) => lookupProductData(item.product_id, null).catch(() => null)),
  );

  const items = cartContents.items.map((cartItem, index) => {
    const sellingPlan = cartItem.selling_plan_allocation?.selling_plan
      ? "subscription"
      : "one_time|subscription";
    const currentItemData = itemData[index];

    return {
      item_id: cartItem.product_id,
      item_name: cartItem.product_title,
      index,
      quantity: cartItem.quantity,
      coupon: null,
      discount: cartItem.total_discount,
      item_list_id: currentItemData ? currentItemData.item_list_id : null,
      item_list_name: currentItemData ? currentItemData.item_list_name : null,
      item_category: cartItem.product_type,
      item_category2: currentItemData ? currentItemData.item_category2 : null,
      item_category3: currentItemData ? currentItemData.item_category3 : null,
      item_category4: currentItemData ? currentItemData.item_category4 : null,
      item_category5: sellingPlan,
      item_category6: currentItemData ? currentItemData.item_category6 : null,
      item_variant: camelToUnderscore(cartItem.variant_title),
      price: parseFloat(((cartItem.final_price || 0) / 100).toFixed(2)),
    };
  });

  pushEcommerce({
    event: eventName,
    ecommerce: {
      currency: window.QF.currency,
      value: parseFloat(((cartContents.total_price || 0) / 100).toFixed(2)),
      items,
    },
  });
}

async function trackAddedItem(event) {
  const cartItems = event.detail?.cart?.items;
  const newestItemId = event.detail?.newestItem || event.detail?.variant_id;
  if (!cartItems?.length || !newestItemId) return;

  const newestCartItem = cartItems.find((item) => String(item.id) === String(newestItemId));
  if (!newestCartItem) return;

  const sellingPlan = newestCartItem.selling_plan_allocation?.selling_plan?.id
    ? "subscription"
    : "one_time";

  try {
    const itemData = normalizeProductVariant(
      await lookupProductData(newestCartItem.product_id, sellingPlan),
      newestCartItem.variant_id || newestItemId,
    );
    itemData.index = cartItems.findIndex((item) => String(item.id) === String(newestItemId)) + 1;
    itemData.quantity = event.detail?.quantity || newestCartItem.quantity;

    pushEcommerce({
      event: "add_to_basket",
      ecommerce: {
        currency: window.QF.currency,
        value: itemData.price || "",
        items: [itemData],
      },
    });

    var _learnq = window._learnq || [];
    var cart = {
      total_price: event.detail.cart.total_price / 100,
      $value: event.detail.cart.total_price / 100,
      total_discount: event.detail.cart.total_discount,
      original_total_price: event.detail.cart.original_total_price / 100,
      items: event.detail.cart.items,
    };
    _learnq.push(["track", "Added to Cart", cart]);
  } catch (error) {
    console.error("Tracking: unable to track add to basket", error);
  }
}

async function trackRemovedItem(event) {
  const productId = event.detail?.product_id;
  const variantId = event.detail?.variant_id;
  if (!productId) return;

  try {
    const itemData = normalizeProductVariant(await lookupProductData(productId, null), variantId);
    itemData.index = parseInt(event.detail.index || 0, 10);
    itemData.quantity = parseInt(event.detail.quantity || 1, 10);

    pushEcommerce({
      event: "remove_from_cart",
      ecommerce: {
        currency: window.QF.currency,
        value: itemData.price || "",
        items: [itemData],
      },
    });
  } catch (error) {
    console.error("Tracking: unable to track remove from cart", error);
  }
}

function getSearchTerm() {
  return (
    document.querySelector(".search-grid")?.getAttribute("data-search-terms") ||
    document.querySelector("[name='q']")?.value ||
    document.querySelector(".search-input")?.value ||
    document.querySelector(".predictive-search-form__input")?.value ||
    ""
  );
}

function trackProductCardClick(event) {
  const productCard = event.target.closest(".product-card[data-product-id]");
  if (!productCard) return;

  const href =
    productCard.getAttribute("href") ||
    productCard.querySelector("a[href]")?.getAttribute("href") ||
    "";
  const id = productCard.getAttribute("data-product-id");

  if (event.target.closest("predictive-search, #predictive-search-results")) {
    pushEcommerce({
      event: "search",
      ecommerce: {
        search_keywords: getSearchTerm(),
        search_type: "predictive",
        search_item: href,
      },
    });
  }

  if (document.querySelector(".search-grid, .search-results-container, .product-grid")) {
    pushEcommerce({
      event: "search",
      ecommerce: {
        search_keywords: window.QF.search_terms || getSearchTerm(),
        search_type: "main-search",
        search_item: href,
      },
    });
  }

  lookupProductData(id, null)
    .then((itemData) => {
      const item = normalizeProductVariant(itemData, productCard.getAttribute("data-variant-id"));
      item.index = parseInt(productCard.getAttribute("data-index") || "0", 10);

      pushEcommerce({
        event: "select_item",
        ecommerce: {
          currency: window.QF.currency,
          value: item.price || "",
          items: [item],
        },
      });
    })
    .catch((error) => console.error("Tracking: unable to track product selection", error));
}

function trackFaqClick(event) {
  const legacyFaq = event.target.closest(".faq-accordion.active");
  const accordion = event.target.closest(".accordion, details, [data-accordion]");
  const element = legacyFaq || accordion;

  if (!element) return;

  const parentGroup = event.target.closest(".accordion-faqs-group, section, [data-section-title]");
  const category =
    parentGroup?.querySelector("h2")?.textContent?.trim() ||
    parentGroup?.getAttribute("data-section-title") ||
    "";
  const question =
    event.target.closest("summary, button, .accordion-header, .question")?.textContent?.trim() ||
    event.target.textContent?.trim() ||
    "";

  if (!question) return;

  pushEcommerce({
    event: "select_faq",
    ecommerce: {
      category,
      questions: question,
    },
  });
}

function trackFindMyFood(event) {
  if (!event.detail) return;

  let quizType = "feeding_plan";
  if (window.template === "page.build-a-box" || window.template === "page.build-a-box-landing") {
    quizType = "build_a_box";
  }

  pushEcommerce({
    event: "find_my_food",
    ecommerce: {
      find_my_food_breed: normalizeValue(event.detail.dog_breed),
      find_my_food_age: normalizeValue(event.detail.dog_age),
      find_my_food_weight_current: normalizeValue(event.detail.dog_weight_current),
      find_my_food_preferences: normalizeValue(event.detail.dog_preferences),
      find_my_food_favourites: normalizeValue(event.detail.dog_favourites),
      find_my_food_activity: normalizeValue(event.detail.dog_activity),
      find_my_food_recommendation: true,
      quiz_type: quizType,
    },
  });
}

function trackPetPassport(event) {
  if (!event.detail) return;

  pushEcommerce({
    event: "pet_passport",
    ecommerce: {
      pet_passport_breed: event.detail.dog_breed,
      pet_passport_age: event.detail.dog_age,
      pet_passport_weight_current: event.detail.dog_weight_current,
      pet_passport_preferences: event.detail.dog_preferences,
      pet_passport_favourites: event.detail.dog_favourites,
      pet_passport_activity: event.detail.dog_activity,
      pet_passport_recommendation: true,
      pet_passport_fur_siblings: event.detail.dog_siblings,
      pet_passport_info: [
        event.detail.dog_gender,
        event.detail.dog_rescue,
        event.detail.dog_neutered,
      ].join("|"),
    },
  });
}

function observeReviewsSection() {
  const reviewsSection = document.querySelector(
    ".trustpilot-widget, .top-reviews, #product-reviews, [data-product-reviews]",
  );
  if (!reviewsSection || !("IntersectionObserver" in window)) return;

  let hasPushedEvent = false;
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || hasPushedEvent) return;

      setTimeout(() => {
        pushEcommerce({
          event: "reviews_widget_viewed",
          ecommerce: {
            product_id: window.QF.product?.id,
            variant_id: window.QF.variant_id,
            sku: window.QF.sku,
            number_of_stars: window.QF.number_of_stars,
            number_of_reviews: window.QF.number_of_reviews,
          },
        });
        hasPushedEvent = true;
      }, 5000);
    });
  });

  observer.observe(reviewsSection);
}

function bindReviewStars() {
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[trustpilot-review-stars], .meta-review")) return;

    pushEcommerce({
      event: "reviews_stars_clicked",
      ecommerce: {
        product_id: window.QF.product?.id,
        variant_id: window.QF.variant_id,
        sku: window.QF.sku,
        number_of_stars: window.QF.number_of_stars,
        number_of_reviews: window.QF.number_of_reviews,
      },
    });
  });
}

function bindEvents() {
  window.addEventListener("quickview_opened", trackQuickView);
  window.addEventListener("side_cart_opened", (event) => {
    void cartDataLayerPush("view_cart", event.detail?.cart);
  });
  window.addEventListener("item_added", (event) => void trackAddedItem(event));
  window.addEventListener("cart_item_removed", (event) => void trackRemovedItem(event));
  window.addEventListener("find_my_food", trackFindMyFood);
  window.addEventListener("pet_passport", trackPetPassport);

  window.addEventListener("klaviyoForms", (event) => {
    if (event.detail?.type !== "submit") return;

    const method = NEWSLETTER_FORM_METHODS[event.detail.formId];
    if (!method) return;

    document.dispatchEvent(new CustomEvent("user_signup", { detail: { method } }));
  });

  document.addEventListener("loaded_more_products", () => {
    fetchProductCards();
  });

  document.addEventListener("user_signup", (event) => {
    pushEcommerce({
      event: "sign_up",
      ecommerce: {
        method: event.detail?.method || "create_account",
      },
    });
  });

  document.addEventListener("modal_opened", (event) => {
    pushEcommerce({
      event: "pop_up_type",
      ecommerce: {
        method: event.detail?.id || "",
      },
    });
  });

  document.addEventListener("user_search", (event) => {
    pushEcommerce({
      event: "search",
      ecommerce: {
        search_keywords: event.detail?.keyword,
      },
    });
  });

  document.addEventListener("contact_us", (event) => {
    pushEcommerce({
      event: "contact_us",
      ecommerce: {
        method: event.detail?.type || "contact",
      },
    });
  });

  document.addEventListener("submit", (event) => {
    const form = event.target;
    const action = form?.getAttribute?.("action") || "";

    if (action.includes("/search")) {
      const keyword = form.querySelector("[name='q']")?.value || getSearchTerm();
      document.dispatchEvent(new CustomEvent("user_search", { detail: { keyword } }));
    }

    if (action.includes("/contact") || form.querySelector("[name='contact[body]']")) {
      document.dispatchEvent(new CustomEvent("contact_us", { detail: { type: "contact" } }));
    }
  });

  document.addEventListener("click", (event) => {
    trackProductCardClick(event);
    trackFaqClick(event);
  });
}

function trackFindStockist() {
  if (!window.location.pathname.startsWith("/pages/find-a-stockist")) return;

  pushEcommerce({
    event: "find_stockist",
    ecommerce: {
      location: "",
      distance: 0,
    },
  });
}

function track404() {
  if (window.template === "404" || window.templateName === "404") {
    window.dataLayer.push({
      event: "exception",
      ecommerce: {
        description: "404",
        error_url: window.location.pathname,
      },
    });
  }
}

function setBuildABoxAnswers() {
  const params = new URLSearchParams(window.location.search);
  const dogId = parseInt(params.get("dogId") || "0", 10);
  if (!dogId) return;

  const passportDogData = readJsonStorage("passport", []);
  const dogData = passportDogData.find((dog) => parseInt(dog.id, 10) === dogId);

  window.QF.dogId = dogId;
  if (!dogData) return;

  window.QF.bab_answers = [
    dogData.dog_name,
    dogData.dog_breed,
    dogData.dog_age,
    dogData.dog_preferences,
    dogData.dog_favourites,
    dogData.dog_activity,
  ]
    .join("|")
    .replace(/-/g, "_")
    .replace(/ /g, "_")
    .toLowerCase();
}

async function initTracking() {
  setBuildABoxAnswers();
  trackUserState();
  trackFormHash();
  bindEvents();
  bindReviewStars();
  track404();
  trackFindStockist();

  await storeProductData();
  observeSections();
  fetchProductCards();
  await trackProductPageView();

  if (window.templateName === "product") {
    observeReviewsSection();
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void initTracking(), { once: true });
} else {
  void initTracking();
}
